import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    FaBell,
    FaCalendarAlt,
    FaChevronDown,
    FaChevronRight,
    FaChevronUp,
    FaExchangeAlt,
    FaInfoCircle,
    FaMapMarkerAlt,
    FaPaperPlane,
    FaPlane,
    FaPlus,
    FaSave,
    FaStickyNote,
    FaTrash,
    FaUndo,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import CabecalhoEventoAereo from '../../components/CabecalhoEventoAereo';
import { useAtendimentoAereo } from '../../components/AtendimentoAereo';
import ColarOrigens from '../../components/ColarOrigens';
import Data from '../../components/Data';
import ErpTemplate from '../../components/ErpTemplate';
import { FinCheck, FinField, FinInput, FinNumber, FinSwitch, FinTextarea } from '../../components/FinField';
import api from '../../services/api';
import notify from '../../services/notify';
import ConfirmarAcaoModal, { ObservacaoObrigatoria } from '../../components/ConfirmarAcaoModal';
import FormTrecho from '../../components/FormTrecho';
import PillObservacao from '../../components/PillObservacao';
import { cuidaDoPedido, abrePedido, chaveOrigem, assinaturaDoTrecho, trechoParaGravar, HORARIOS, TODOS_HORARIOS, EXEMPLO_LOCAL, LOCAL_ACEITO, LOCAL_MIN, LOCAIS, TODOS_CAMPOS_LOCAL, ABERTURAS_PADRAO, abrirPernaDosLocais, trechoEfetivo, trechoParaTela, comDirecaoLigada, comSemTrecho, comLocalDigitado, pedeIda, pedeRetorno, pontasDoVinculo, rotaDoTrecho, resumoDoTrecho, ROTULO_SITUACAO_SOLICITACAO, COR_SITUACAO_SOLICITACAO, PERFIS_AEREO } from '../../util/malhaAereo';
import '../../css/financeiro-parcela-pagar.css';

/**
 * Solicitação de Malha Aérea — o pedido por dentro, em duas abas:
 *
 *   Origens → destino, data de ida e de volta e observação sobre horários, iguais para todas
 *             as origens; e as origens, coladas de uma vez
 *   Trechos → detalhe opcional: um trecho preso a uma origem (ou solto) troca o que precisar;
 *             a aba agrupa os trechos pela origem
 *
 * Campo vazio no trecho vale o da origem (texto) e o da solicitação (destino e datas) — a tela
 * mostra o valor herdado. Tudo vai num Salvar só; origem nova chega ao back com chave
 * temporária, e o trecho que a escolheu aponta para essa chave. Edita só quem cuida do pedido
 * (RSVP e admin); o Aéreo e Eventos leem.
 */

let _nextKey = 1;
const novaChave = () => `novo-${_nextKey++}`;

// Rótulo de campo numa linha só, com reticências quando não cabe: quebrado, ele deixava o campo
// mais alto que os vizinhos da mesma fileira.
// O badge do herdado não trunca: não encolhe (`flexShrink: 0`) e não tem o teto de 60% da linha
// que o `.fin-pp__herdado` aplica — com ele, o badge era cortado antes de o campo chegar ao piso.
// Quem cede espaço no cabeçalho é o rótulo, com reticências.
// Alerta de uma linha, na largura do texto: o .fin-pp__banner sozinho ocupa a linha inteira.
const ALERTA_CURTO = { display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 14px', flex: '0 0 auto', whiteSpace: 'nowrap' };
const SUBLINHADO = { textDecoration: 'underline', textUnderlineOffset: 1 };

// Os campos de cada direção. O switch da direção só abre esses campos na tela. Fechada, ela não leva
// nenhum deles ao banco e vale o herdado — o valor fica só na tela, para voltar se a direção for
// reaberta antes de salvar. Se a direção é pedida, quem diz é o "Sem trecho" ao lado do switch.
const CAMPOS_IDA     = ['dataIda', ...HORARIOS.ida.flatMap(h => [h.de, h.ate])];
const CAMPOS_RETORNO = ['dataRetorno', ...HORARIOS.retorno.flatMap(h => [h.de, h.ate])];

const PEDIDO_VAZIO = { destino: '', dataIda: '', dataRetorno: '', observacao: '' };

const CELULA_NO_MEIO = { verticalAlign: 'middle' };

const texto = (v) => String(v ?? '');

// Placeholder do campo vazio com o badge do herdado desligado: o campo não vale o herdado.

const origemDoServidor = (o) => ({
    _key: String(o.id),
    id: String(o.id),
    origem: texto(o.origem),
    quantidadePassageiros: o.quantidadePassageiros ?? null,
});

function createEmptyTrecho(prefill = {}) {
    return {
        _key: novaChave(),
        id: null,
        origem_id: null,
        origem: '',
        destino: '',
        // Campos próprios da volta, gravados: escritos, a volta tem lugar próprio; vazios, ela
        // espelha a ida — sai de onde a ida chega e chega de onde ela saiu.
        origemVolta: '',
        destinoVolta: '',
        // Só de tela, como os switches de horário: qual perna tem campo aberto em cada ponta. Não
        // vai ao banco — o que fica gravado é o texto, e campo fechado grava vazio no Salvar.
        ...ABERTURAS_PADRAO,
        // Badge do herdado: nasce ligado e só desliga no clique; apagar o texto religa. Ligado, o campo
        // vazio vale o herdado e o escrito o detalha entre parênteses; desligado, o escrito troca o
        // herdado e o vazio fica sem lugar. Cada um dos quatro campos tem o seu.
        origemHerdada: true,
        destinoHerdado: true,
        origemVoltaHerdada: true,
        destinoVoltaHerdado: true,
        // Direções pedidas, gravadas: false é o "Sem trecho de Ida" (ou de Volta) marcado ao lado do
        // switch. Sem a marcação, a direção vai pedida mesmo com o switch desligado.
        ida: true,
        retorno: true,
        // Switches só da tela: o trecho nasce com direções e horários fechados, valendo o herdado.
        detalharIda: false,
        detalharRetorno: false,
        ...Object.fromEntries(TODOS_HORARIOS.map(h => [h.abrir, false])),
        ...Object.fromEntries([...CAMPOS_IDA, ...CAMPOS_RETORNO].map(c => [c, ''])),
        quantidadePassageiros: null,
        observacao: '',
        expanded: true,
        ...prefill,
    };
}

// Valores, marcas de tela e defaults saem do `trechoParaTela`, que o modal da malha usa igual —
// aqui entram só as chaves desta tela.
const trechoDoServidor = (t) => ({
    ...trechoParaTela(t),
    _key: String(t.id),
    id: String(t.id),
    origem_id: t.origem_id ? String(t.origem_id) : null,
    expanded: false,
});

// Por que o trecho não pode ser gravado; null quando pode. Espelha a validação do back. Nada no
// trecho é obrigatório (vazio vale o herdado, e sem direção marcada ele pede as duas): só o texto
// escrito precisa ter ao menos 2 letras.
function motivoTrechoInvalido(t) {
    const origem = t.origem.trim();
    if (origem && origem.length < LOCAL_MIN) return 'origem com menos de 2 letras';
    const destino = t.destino.trim();
    if (destino && destino.length < LOCAL_MIN) return 'destino com menos de 2 letras';
    // Sem origem da solicitação e sem origem própria, a ida não tem de onde partir: o trecho nasce
    // sem ponta de partida, e o vínculo automático nunca casa nada com ele. O destino não cai nesse
    // caso — vazio, ele vale o do pedido, que é obrigatório.
    if (pedeIda(t) && !t.origem_id && !texto(trechoEfetivo(t).origem).trim()) {
        return 'sem origem: escolha a origem da solicitação ou escreva uma';
    }
    // Preso a uma origem, vazio vale a quantidade dela. Solto, não há de quem herdar: o trecho
    // entraria na malha sem contagem nenhuma, e é por ela que o Aéreo cota.
    if (!t.origem_id && !(t.quantidadePassageiros > 0)) {
        return 'trecho solto: informe a quantidade de passageiros';
    }
    return null;
}

export default function CadastroEventoSolicitacaoAereo({ match }) {
    const history  = useHistory();
    // A rota passa o id da SOLICITAÇÃO, não o do evento: um evento pode ter vários pedidos, e
    // esta tela edita UM. O id do evento chega no cabeçalho, porque é dele que os atalhos precisam.
    const idSolicitacao = match.params.idSolicitacao;
    const perfil   = localStorage.getItem('perfil');
    const token    = localStorage.getItem('token');
    const nomeUsuario = localStorage.getItem('nomeUsuario');
    // Quem abre o pedido edita: o RSVP, Eventos e o admin. O Aéreo lê.
    const cuidaDaSolicitacao = abrePedido(perfil);

    // A aba vai na URL (?aba=trechos): trocar de aba entra no histórico, e a seta do navegador
    // volta para a aba anterior.
    const location = useLocation();
    const aba = new URLSearchParams(location.search).get('aba') === 'trechos' ? 'trechos' : 'origens';
    const setAba = (nova) => {
        if (nova !== aba) history.push({ search: nova === 'trechos' ? '?aba=trechos' : '' });
    };

    const [headerEvento, setHeaderEvento] = useState({});
    // O cabeçalho do pedido: a situação e se o Aéreo pode pedir alteração.
    const carregarCabecalho = useCallback(() => api({ method: 'get', action: '/eventoAereoCabecalho', token, params: { solicitacao: idSolicitacao } })
        .then(res => setHeaderEvento(res.data?.data ?? {}))
        .catch(() => {}), [idSolicitacao, token]);
    // Atendimento do Aéreo: aqui ele lê a solicitação e pede alteração, e o modal oferece assumir o
    // evento, como em toda tela do fluxo. Assumir relê o cabeçalho, que libera o Pedir alteração.
    const atendimento = useAtendimentoAereo(headerEvento.evento, carregarCabecalho);
    const [pedido, setPedido]             = useState(PEDIDO_VAZIO);
    const [origens, setOrigens]           = useState([]);
    const [trechos, setTrechos]           = useState([]);
    const [salvando, setSalvando]         = useState(false);
    const [enviando, setEnviando]         = useState(false);
    // Enviar passa a vez para o Aéreo: confirma antes, para um clique errado não mandar.
    const [confirmarEnvio, setConfirmarEnvio] = useState(false);
    // Notificar alteração: a confirmação aberta e a observação do e-mail. `alteradoPor` é a marcação
    // de edição depois do envio, pelo grupo ("RSVP"), que o notificar apaga.
    const [alteradoPor, setAlteradoPor] = useState(null);
    const [confirmarNotificacao, setConfirmarNotificacao] = useState(false);
    const [observacaoNotificacao, setObservacaoNotificacao] = useState('');
    const [notificando, setNotificando] = useState(false);
    // Reabrir a cancelada ou a concluída, com a observação do e-mail: o mesmo do painel de Solicitações.
    const [confirmarReabertura, setConfirmarReabertura] = useState(false);
    const [observacaoReabertura, setObservacaoReabertura] = useState('');
    const [reabrindo, setReabrindo] = useState(false);
    // Pedir alteração: do Aéreo, com a observação no e-mail ao RSVP.
    const [confirmarAlteracao, setConfirmarAlteracao] = useState(false);
    const [observacaoAlteracao, setObservacaoAlteracao] = useState('');
    const [pedindoAlteracao, setPedindoAlteracao] = useState(false);
    // Cópia do payload na última leitura ou gravação: a tela mudou quando o de agora é outro.
    // `fotografar` pede a cópia depois que o conteúdo novo entrou no estado.
    const [gravado, setGravado] = useState(null);
    const [fotografar, setFotografar] = useState(0);
    // Trecho que acabou de mudar de lugar na aba (criado, ou trocado de origem): a tela rola até
    // ele, senão o card some de vista ao pular de grupo.
    const [seguirTrecho, setSeguirTrecho] = useState(null);
    // Trecho cuja observação recebe o foco, pedido pelo "Observação" da linha da origem.
    const [focarObservacao, setFocarObservacao] = useState(null);
    // "Destino e datas" abre e fecha. Abre fechado quando o obrigatório já veio preenchido: o
    // cabeçalho mostra os valores.
    const [pedidoAberto, setPedidoAberto] = useState(true);
    // Acordeão de cada origem na aba Trechos: guarda só o que foi aberto ou fechado à mão, pela
    // chave da origem. Sem escolha, abre o grupo que tem trecho.
    const [grupoAberto, setGrupoAberto] = useState({});
    // A caixa de colar do card da solicitação aparece sem nenhuma origem, com foco ou com texto
    // digitado; some com a janela dos separadores aberta. A do card de Origens fica sempre.
    const [colarFocado, setColarFocado] = useState(false);
    const [colarComTexto, setColarComTexto] = useState(false);
    const [perguntandoSeparador, setPerguntandoSeparador] = useState(false);
    // Carga inicial: o conteúdo e o cabeçalho, cuja situação decide os botões do rodapé. O overlay
    // do ErpTemplate cobre a tela até os dois voltarem, com erro ou não.
    const [carregando, setCarregando] = useState(true);

    const aplicarConteudo = (d) => {
        const p = d?.pedido ?? {};
        setPedido({
            destino: texto(p.destino),
            dataIda: texto(p.dataIda),
            dataRetorno: texto(p.dataRetorno),
            observacao: texto(p.observacao),
        });
        setOrigens((d?.origens ?? []).map(origemDoServidor));
        setTrechos((d?.trechos ?? []).map(trechoDoServidor));
        setAlteradoPor(d?.alteradoPor ?? null);
        setFotografar(n => n + 1);
    };

    useEffect(() => {
        setCarregando(true);
        const conteudo = api({ method: 'get', action: '/eventoAereoTrecho', token, params: { solicitacao: idSolicitacao } })
            .then(res => {
                const d = res.data?.data;
                aplicarConteudo(d);
                setPedidoAberto(!['destino', 'dataIda', 'dataRetorno'].every(c => texto(d?.pedido?.[c]).trim()));
            })
            .catch(err => notify.apiError(err, 'Erro ao carregar a solicitação'));
        const cabecalho = carregarCabecalho();
        // Os dois já tratam o erro, então o Promise.all sempre resolve e o overlay sempre sai.
        Promise.all([conteudo, cabecalho]).finally(() => setCarregando(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idSolicitacao]);

    useEffect(() => {
        if (!seguirTrecho) return;
        document.getElementById('trecho-' + seguirTrecho)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setSeguirTrecho(null);
    }, [seguirTrecho]);

    // A observação só existe na tela depois que a aba, o grupo e o card abrem: sem ela ainda, o
    // pedido de foco espera a próxima renderização.
    useEffect(() => {
        if (!focarObservacao) return;
        const campo = document.getElementById('observacao-trecho-' + focarObservacao);
        if (!campo) return;
        campo.scrollIntoView({ behavior: 'smooth', block: 'center' });
        campo.focus({ preventScroll: true });
        setFocarObservacao(null);
    }, [focarObservacao, aba, trechos, grupoAberto]);

    // Trocar de aba recolhe os acordeões: os trechos fecham, os grupos de origem voltam ao padrão
    // (abertos quando têm trecho) e "Destino e datas" fecha se o obrigatório está preenchido. Quem troca
    // de aba para abrir algo (o "Observação" da origem, a conferência do Salvar) deixa em
    // `aoTrocarDeAba` o que abrir depois do recolhimento; a seta do navegador só recolhe.
    const aoTrocarDeAba = useRef(null);
    const abaAnterior = useRef(aba);
    useEffect(() => {
        if (abaAnterior.current === aba) return;
        abaAnterior.current = aba;
        const abrir = aoTrocarDeAba.current ?? {};
        aoTrocarDeAba.current = null;
        const trechosAbertos = new Set(abrir.trechos ?? []);
        setTrechos(prev => prev.map(t => (!!t.expanded === trechosAbertos.has(t._key)
            ? t
            : { ...t, expanded: trechosAbertos.has(t._key) })));
        setGrupoAberto(Object.fromEntries((abrir.grupos ?? []).map(c => [chaveGrupo(c), true])));
        setPedidoAberto(!!abrir.pedido || !obrigatorioPreenchido());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aba]);

    useEffect(() => {
        if (fotografar) setGravado(JSON.stringify(payload()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fotografar]);

    const situacaoSolicitacao = headerEvento.situacaoSolicitacao;
    // Enviar é do rascunho. Depois do envio, quem fala com o Aéreo é o "Notificar alteração", a
    // qualquer momento, com ou sem mudança — em alteração, ele devolve a solicitação ao Aéreo.
    const podeEnviar = cuidaDaSolicitacao && situacaoSolicitacao === 'pendente';
    const podeNotificar = cuidaDaSolicitacao && ['solicitada', 'concluida', 'alteracao'].includes(situacaoSolicitacao);
    // Reabrir: a cancelada ou a concluída volta em alteração (a mesma regra do back).
    const podeReabrir = cuidaDaSolicitacao && ['cancelada', 'concluida'].includes(situacaoSolicitacao);
    // Quem diz se o Aéreo pede alteração é o back (null = pode): o Aéreo que atende o evento, com a
    // solicitação enviada. Vale também já em alteração, para mandar outra observação.
    const podePedirAlteracao = headerEvento.motivoNaoPedirAlteracao === null;
    // Cancelada não se mexe mais: a tela vira leitura (o back recusa de todo jeito).
    const cancelada = situacaoSolicitacao === 'cancelada';
    const editavel = cuidaDaSolicitacao && !cancelada;
    // Depois do envio, a solicitação não fica vazia — é o que o Aéreo cota. O rascunho pode
    // esvaziar: só vazio ele se exclui.

    const origemPorChave = useMemo(() => new Map(origens.map(o => [o._key, o])), [origens]);
    // Quanta gente a origem do trecho pediu: é o valor que o campo vazio do trecho vale, e o
    // máximo que ele pode levar. Trecho solto não tem teto — não há de quem herdar.
    const tetoDePassageiros = (t) => (t.origem_id
        ? origemPorChave.get(t.origem_id)?.quantidadePassageiros ?? null
        : null);
    const trechosPorOrigem = useMemo(() => {
        const contagem = new Map();
        trechos.forEach(t => { if (t.origem_id) contagem.set(t.origem_id, (contagem.get(t.origem_id) ?? 0) + 1); });
        return contagem;
    }, [trechos]);

    // A aba Trechos mostra toda origem, com ou sem trecho, na ordem das origens; o trecho solto
    // fica num grupo no fim, que só aparece quando há algum. Dentro do grupo vale a ordem da
    // lista, que é a que o Salvar grava.
    const gruposTrechos = useMemo(() => {
        const porOrigem = new Map();
        const soltos = [];
        trechos.forEach(t => {
            if (t.origem_id && origemPorChave.has(t.origem_id)) {
                if (!porOrigem.has(t.origem_id)) porOrigem.set(t.origem_id, []);
                porOrigem.get(t.origem_id).push(t);
            } else {
                soltos.push(t);
            }
        });
        const grupos = origens
            .map(o => ({
                chave: o._key, nome: o.origem.trim() || '(sem nome)', passageiros: o.quantidadePassageiros ?? null,
                trechos: porOrigem.get(o._key) ?? [],
            }));
        if (soltos.length) grupos.push({ chave: null, nome: 'Sem origem', trechos: soltos });
        return grupos;
    }, [origens, trechos, origemPorChave]);

    const chaveGrupo = (chave) => chave ?? 'sem-origem';
    const grupoEstaAberto = (g) => grupoAberto[chaveGrupo(g.chave)] ?? g.trechos.length > 0;
    const alternarGrupo = (g) => setGrupoAberto(prev => ({ ...prev, [chaveGrupo(g.chave)]: !grupoEstaAberto(g) }));
    // Trecho criado, movido ou com pendência precisa do grupo aberto para aparecer.
    const abrirGrupos = (chaves) => setGrupoAberto(prev => ({
        ...prev,
        ...Object.fromEntries(chaves.map(c => [chaveGrupo(c), true])),
    }));
    // Vai para a aba já com o que abrir: `{ trechos, grupos, pedido }`. Mudando de aba, o pedido espera
    // o recolhimento da troca; na mesma aba, abre na hora.
    const irParaAba = (nova, abrir = {}) => {
        if (nova !== aba) {
            aoTrocarDeAba.current = abrir;
            setAba(nova);
            return;
        }
        if (abrir.trechos?.length) {
            const chaves = new Set(abrir.trechos);
            setTrechos(prev => prev.map(t => (chaves.has(t._key) ? { ...t, expanded: true } : t)));
        }
        if (abrir.grupos?.length) abrirGrupos(abrir.grupos);
        if (abrir.pedido) setPedidoAberto(true);
    };

    // --- Payload e gravação ---

    // A máscara do campo de data devolve "__/__/____" quando se apaga: isso é vazio, e vazio no
    // trecho é "vale o da solicitação".
    const dataLimpa = (v) => (texto(v).includes('_') ? '' : texto(v).trim());
    // Destino e as duas datas do pedido preenchidos: "Destino e datas" pode ficar fechado.
    const obrigatorioPreenchido = () => !!(pedido.destino.trim() && dataLimpa(pedido.dataIda) && dataLimpa(pedido.dataRetorno));

    const payload = () => ({
        pedido: { ...pedido, dataIda: dataLimpa(pedido.dataIda), dataRetorno: dataLimpa(pedido.dataRetorno) },
        origens: origens.map(o => ({
            id: o.id,
            // Origem nova vai com a chave da tela: é por ela que o trecho a encontra.
            chave: o.id ? undefined : o._key,
            origem: o.origem.trim(),
            quantidadePassageiros: o.quantidadePassageiros,
        })),
        trechos: trechos.map(({ expanded: _, _key: __, solicitante: ___, ...t }) => {
            // O que as marcas de tela escondem não vai ao banco, e essa regra é uma só —
            // `trechoParaGravar`, a mesma que o modal da malha usa. Aqui ficam só as decisões do
            // PEDIDO: a direção vai pedida, menos com "Sem trecho" marcado, e os badges do herdado
            // viram booleano de verdade.
            const limpo = {
                ...trechoParaGravar(t), ida: t.ida !== false, retorno: t.retorno !== false,
                origemHerdada: !!t.origemHerdada,
                destinoHerdado: !!t.destinoHerdado,
                origemVoltaHerdada: !!t.origemVoltaHerdada,
                destinoVoltaHerdado: !!t.destinoVoltaHerdado,
            };
            limpo.dataIda = dataLimpa(limpo.dataIda);
            limpo.dataRetorno = dataLimpa(limpo.dataRetorno);
            return limpo;
        }),
    });

    // A tela mudou desde a última leitura ou gravação: o Notificar salva antes.
    const mudou = gravado !== null && JSON.stringify(payload()) !== gravado;

    // Salva tudo. Devolve a promise para o Enviar encadear: enviar sem gravar mandaria ao Aéreo o
    // pedido de antes da edição.
    const persistir = () => api({ method: 'put', action: '/eventoAereoTrecho/' + idSolicitacao, token, data: payload() })
        .then(res => {
            aplicarConteudo(res.data?.data);
            toast.success('Solicitação salva');
            return res;
        });

    // Confere antes de gravar — inclusive os trechos recolhidos, que a validação nativa não
    // alcança. Leva à aba do problema e abre os trechos com pendência.
    const conferir = () => {
        // Obrigatórios: destino, data de ida, data de volta e ao menos uma origem.
        const destinoPedido = pedido.destino.trim();
        const faltam = [
            !destinoPedido && 'o destino',
            !dataLimpa(pedido.dataIda) && 'a data de ida',
            !dataLimpa(pedido.dataRetorno) && 'a data de volta',
        ].filter(Boolean);
        if (faltam.length) {
            irParaAba('origens', { pedido: true });
            const ultimo = faltam.pop();
            toast.error(`Informe ${faltam.length ? `${faltam.join(', ')} e ` : ''}${ultimo} da solicitação.`);
            return false;
        }
        if (destinoPedido.length < LOCAL_MIN) {
            irParaAba('origens', { pedido: true });
            toast.error('Destino da solicitação com menos de 2 letras.');
            return false;
        }
        if (!origens.length) {
            irParaAba('origens');
            toast.error('Informe ao menos uma origem.');
            return false;
        }
        // A quantidade de passageiros da origem é opcional; informada, é ao menos 1.
        const quantidadeZero = origens.filter(o => o.quantidadePassageiros != null && !(o.quantidadePassageiros >= 1))
            .map(o => o.origem.trim() || '(sem nome)');
        if (quantidadeZero.length) {
            irParaAba('origens');
            toast.error('A quantidade de passageiros, quando informada, é ao menos 1: ' + (quantidadeZero.length > 5
                ? `${quantidadeZero.slice(0, 5).join(', ')} e mais ${quantidadeZero.length - 5}.`
                : `${quantidadeZero.join(', ')}.`));
            return false;
        }
        const curtas = origens.filter(o => o.origem.trim().length < LOCAL_MIN);
        if (curtas.length) {
            irParaAba('origens');
            toast.error('Origem com menos de 2 letras.');
            return false;
        }
        const vistas = new Set();
        const repetida = origens.find(o => {
            const chave = chaveOrigem(o.origem);
            if (vistas.has(chave)) return true;
            vistas.add(chave);
            return false;
        });
        if (repetida) {
            irParaAba('origens');
            toast.error(`A origem "${repetida.origem.trim()}" está duas vezes na solicitação.`);
            return false;
        }
        // Dois trechos idênticos na mesma origem seriam dois vínculos indistinguíveis, e a opção
        // ficaria órfã por empate no upload. Compara a ASSINATURA — locais, badges, direções, datas
        // e horas —, que deixa de fora a observação e a quantidade: recado e contagem não mudam o
        // que se está pedindo. Vale também para o trecho neutro: ele é permitido, mas só um por
        // origem, senão o desempate do vínculo automático volta a ser adivinhação.
        const assinaturas = new Map();
        for (const [i, t] of trechos.entries()) {
            const chave = `${t.origem_id ?? ''}|${assinaturaDoTrecho(trechoEfetivo(t))}`;
            const antes = assinaturas.get(chave);
            if (antes !== undefined) {
                irParaAba('trechos', {
                    trechos: [trechos[antes]._key, t._key],
                    grupos: [trechos[antes].origem_id, t.origem_id],
                });
                toast.error(`O trecho ${i + 1} é igual ao trecho ${antes + 1}: mude algum campo ou remova um dos dois.`);
                return false;
            }
            assinaturas.set(chave, i);
        }
        // Na ordem e com o título que a aba mostra ("São Paulo ⇄ Salvador"); os cards com pendência
        // abrem, o que desfaz a dúvida entre títulos iguais.
        const pendentes = gruposTrechos
            .flatMap(g => g.trechos.map((t, i) => ({
                t, grupo: g.chave, rotulo: tituloTrecho(t, i), motivo: motivoTrechoInvalido(t),
            })))
            .filter(p => p.motivo);
        if (pendentes.length) {
            irParaAba('trechos', { trechos: pendentes.map(p => p.t._key), grupos: pendentes.map(p => p.grupo) });
            toast.error('Complete antes de salvar — ' + pendentes
                .map(p => `${p.rotulo}: ${p.motivo}`)
                .join('; '));
            return false;
        }
        return true;
    };

    const salvar = () => {
        if (!conferir()) return;
        setSalvando(true);
        persistir()
            // apiError extrai a mensagem por campo do 422.
            .catch(err => notify.apiError(err, 'Erro ao salvar a solicitação'))
            .finally(() => setSalvando(false));
    };

    // Enviar ao Aéreo SALVA antes: o que vai para o Aéreo é o que está na tela. Se o Salvar
    // falhar, não envia.
    const enviar = async () => {
        if (!conferir()) {
            setConfirmarEnvio(false);
            return;
        }
        setEnviando(true);
        try {
            await persistir();
        } catch (err) {
            notify.apiError(err, 'Erro ao salvar a solicitação — ela não foi enviada');
            setEnviando(false);
            setConfirmarEnvio(false);
            return;
        }
        // Enviada, a vez é do Aéreo: volta ao painel de Solicitações, onde está a situação.
        let voltarAoPainel = false;
        api({ method: 'post', action: '/eventoAereoSolicitacaoDecisao', token, data: { solicitacao: idSolicitacao, acao: 'enviar' } })
            .then(res => {
                toast.success('Solicitação enviada ao Aéreo');
                const situacao = res.data?.data?.situacao ?? 'solicitada';
                setHeaderEvento(h => ({ ...h, situacaoSolicitacao: situacao }));
                voltarAoPainel = !!headerEvento.evento;
            })
            .catch(err => notify.apiError(err, 'Erro ao enviar a solicitação'))
            .finally(() => {
                setEnviando(false);
                setConfirmarEnvio(false);
                if (voltarAoPainel) history.push('/cadastroEventoSolicitacoesAereo/' + headerEvento.evento);
            });
    };

    // Notificar alteração SALVA antes, se a tela mudou: o Aéreo é avisado do que está gravado. Com
    // o Salvar feito, abre a confirmação; se ele falhar, não abre.
    const iniciarNotificacao = async () => {
        if (!conferir()) return;
        if (mudou) {
            setSalvando(true);
            try {
                await persistir();
            } catch (err) {
                notify.apiError(err, 'Erro ao salvar a solicitação — a alteração não foi notificada');
                return;
            } finally {
                setSalvando(false);
            }
        }
        setObservacaoNotificacao('');
        setConfirmarNotificacao(true);
    };

    const notificarAlteracao = () => {
        setNotificando(true);
        const data = { solicitacao: idSolicitacao, acao: 'notificar', observacao: observacaoNotificacao };
        api({ method: 'post', action: '/eventoAereoSolicitacaoDecisao', token, data })
            .then(res => {
                toast.success(situacaoSolicitacao === 'alteracao'
                    ? 'Alteração notificada: a solicitação voltou para o Aéreo'
                    : 'Alteração notificada ao Aéreo');
                const situacao = res.data?.data?.situacao ?? situacaoSolicitacao;
                setHeaderEvento(h => ({ ...h, situacaoSolicitacao: situacao }));
                setAlteradoPor(null);
                setConfirmarNotificacao(false);
            })
            .catch(err => notify.apiError(err, 'Erro ao notificar a alteração'))
            .finally(() => setNotificando(false));
    };

    const pedirAlteracao = () => {
        setPedindoAlteracao(true);
        api({ method: 'post', action: '/eventoAereoSolicitacaoDecisao', token, data: { solicitacao: idSolicitacao, acao: 'alteracao', observacao: observacaoAlteracao } })
            .then(res => {
                toast.success('Alteração pedida');
                const situacao = res.data?.data?.situacao ?? 'alteracao';
                setHeaderEvento(h => ({ ...h, situacaoSolicitacao: situacao }));
                setConfirmarAlteracao(false);
            })
            .catch(err => notify.apiError(err, 'Erro ao pedir a alteração'))
            .finally(() => setPedindoAlteracao(false));
    };

    const reabrir = () => {
        setReabrindo(true);
        api({ method: 'post', action: '/eventoAereoSolicitacaoDecisao', token, data: { solicitacao: idSolicitacao, acao: 'reabrir', observacao: observacaoReabertura } })
            .then(res => {
                toast.success('Solicitação reaberta');
                const situacao = res.data?.data?.situacao ?? 'alteracao';
                setHeaderEvento(h => ({ ...h, situacaoSolicitacao: situacao }));
                setAlteradoPor(null);
                setConfirmarReabertura(false);
            })
            .catch(err => notify.apiError(err, 'Erro ao reabrir a solicitação'))
            .finally(() => setReabrindo(false));
    };

    // --- Origens ---

    // `quantidades`: a quantidade de passageiros que veio na colagem, pelo nome da origem.
    const adicionarOrigens = (novas, quantidades = {}) => setOrigens(prev => [
        ...prev,
        ...novas.map(origem => ({ _key: novaChave(), id: null, origem, quantidadePassageiros: quantidades[origem] ?? null })),
    ]);

    // Origem já na lista colada com outra quantidade: troca só a quantidade dela.
    const atualizarQuantidades = (atualizacoes) => setOrigens(prev => prev.map(o => {
        const nova = atualizacoes.find(a => a.chave === chaveOrigem(o.origem));
        return nova ? { ...o, quantidadePassageiros: nova.quantidade } : o;
    }));

    const atualizarOrigem = (key, campo, valor) => setOrigens(prev =>
        prev.map(o => (o._key === key ? { ...o, [campo]: valor } : o)));

    // O trecho preso à origem que sai vira trecho solto (vale ao salvar).
    const removerOrigem = (key) => {
        const presos = trechosPorOrigem.get(key) ?? 0;
        setOrigens(prev => prev.filter(o => o._key !== key));
        if (presos) {
            setTrechos(prev => prev.map(t => (t.origem_id === key ? { ...t, origem_id: null } : t)));
            toast.info(presos === 1 ? 'O trecho preso a ela ficou sem origem.' : `Os ${presos} trechos presos a ela ficaram sem origem.`);
        }
    };

    // --- Trechos ---

    // Sem origem, o trecho nasce solto; pelo botão do grupo, já preso à origem dele.
    const adicionarTrecho = (origemId = null) => {
        const novo = createEmptyTrecho({ origem_id: origemId });
        setTrechos(prev => [...prev, novo]);
        abrirGrupos([origemId]);
        setSeguirTrecho(novo._key);
    };
    // Quanto o trecho troca do herdado: local escrito, campo próprio da volta, badge do herdado
    // desligado, "Sem trecho" marcado, data e horário abertos com valor, passageiros e observação.
    const infoDoTrecho = (bruto) => {
        // Pelo efetivo: campo fechado não conta, mesmo com texto na tela — conta o que será gravado.
        const t = trechoEfetivo(bruto);
        return [
        t.origem.trim(), t.destino.trim(),
        texto(t.origemVolta).trim(), texto(t.destinoVolta).trim(),
        !t.origemHerdada, !t.destinoHerdado,
        t.ida === false, t.retorno === false,
        ...[['detalharIda', 'dataIda', HORARIOS.ida], ['detalharRetorno', 'dataRetorno', HORARIOS.retorno]]
            .flatMap(([abrir, data, horarios]) => (t[abrir]
                ? [dataLimpa(t[data]), ...horarios.flatMap(h => (t[h.abrir] ? [texto(t[h.de]).trim(), texto(t[h.ate]).trim()] : []))]
                : [])),
        t.quantidadePassageiros != null,
        texto(t.observacao).trim(),
        ].filter(Boolean).length;
    };

    // "Observação" na linha da origem: o recado mora no trecho. Leva à aba Trechos, abre o trecho da
    // origem que menos troca do herdado (no empate, o primeiro) ou cria um quando ela não tem nenhum, e
    // põe o foco na observação.
    const observarOrigem = (origemKey) => {
        const existente = trechos
            .filter(t => t.origem_id === origemKey)
            .reduce((menor, t) => (menor && infoDoTrecho(menor) <= infoDoTrecho(t) ? menor : t), null);
        const alvo = existente ?? createEmptyTrecho({ origem_id: origemKey });
        if (!existente) setTrechos(prev => [...prev, alvo]);
        irParaAba('trechos', { trechos: [alvo._key], grupos: [origemKey] });
        setFocarObservacao(alvo._key);
    };
    const removerTrecho = (key) => setTrechos(prev => prev.filter(t => t._key !== key));
    const alternarTrecho = (key) => setTrechos(prev =>
        prev.map(t => (t._key === key ? { ...t, expanded: !t.expanded } : t)));
    // As regras de edição do trecho vivem no FormTrecho: ele devolve o trecho pronto pelo
    // `onTrecho`, e esta tela só guarda. Enquanto elas eram repetidas aqui, o modal da malha
    // tinha a sua versão — e as duas divergiram.
    // O texto da origem pai do trecho, quando ele está preso a uma.
    const origemPaiDe = (t) => (t.origem_id ? texto(origemPorChave.get(t.origem_id)?.origem).trim() : '');

    // As quatro pontas do trecho (origem e destino da ida e da volta). A regra mora em
    // `pontasDoVinculo` (util/malhaAereo): é a mesma que o painel da malha, o casador de opções e
    // as cores usam, e só existe uma cópia dela.
    const pontasTrecho = (t) => pontasDoVinculo(trechoEfetivo(t), { origemPai: origemPaiDe(t), destino: pedido.destino });

    // Título do card: "São Paulo (Congonhas) ⇄ Salvador", pela regra única da rota (`rotaDoTrecho`, a
    // mesma do painel de trechos da malha). Sem lugar nenhum para mostrar, fica "Trecho N".
    const tituloTrecho = (t, i) => rotaDoTrecho(pontasTrecho(t), { ida: t.ida !== false, retorno: t.retorno !== false })
        || `Trecho ${i + 1}`;

    // "Ida 12/03/2026 saída de 08:00 até 10:00 | Volta sem data | 3 passageiros", pela regra única da
    // legenda (`resumoDoTrecho`, a mesma do cabeçalho do modal da malha).
    const resumoTrecho = (t) => resumoDoTrecho(t, { dataIda: pedido.dataIda, dataRetorno: pedido.dataRetorno });

    // Observação do trecho em badge no cabeçalho, como no painel de trechos: o começo na linha e o
    // texto inteiro no hover. Sem observação, nada aparece. Sem espaço, o badge encolhe até o (i)
    // antes de o título e o resumo cortarem: o flex divide a falta de espaço pelo fator vezes a
    // largura, e o fator alto joga quase toda ela no badge.
    // Alerta curto de "opcionais", no centro da linha em que está: os dois lados dele crescem por igual.
    const alertaOpcionais = (conteudo) => (
        <div className="fin-pp__banner is-info" style={ALERTA_CURTO}>
            <FaInfoCircle className="fin-pp__banner-ic" size={13} style={{ marginTop: 0 }} />
            <span>{conteudo}</span>
        </div>
    );

    // Ações do cabeçalho de um trecho: remover e recolher. A barra inteira abre e fecha o card, então
    // o clique aqui para: senão o Remover também abriria e fecharia o trecho, e o recolher alternaria
    // duas vezes.
    const acoesTrecho = (t) => (
        <div className="d-flex gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {editavel && (
                <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                    title="Remove o trecho (vale ao salvar)"
                    onClick={() => removerTrecho(t._key)}>
                    <FaTrash size={11} />
                    Remover
                </button>
            )}
            <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                aria-label={t.expanded ? 'Recolher trecho' : 'Abrir trecho'}
                onClick={() => alternarTrecho(t._key)}>
                {t.expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
            </button>
        </div>
    );

    const vazioVale = (de, valor) => (valor ? { valor, ativo: true, dica: `Vazio vale ${de}: ${valor}` } : null);

    const renderTrecho = (t, i) => {
        return (
            <div key={t._key} id={'trecho-' + t._key} className="fin-pp__trecho-item"
                style={i === 0 ? { borderTop: 0 } : undefined}>
                {/* Aberto, o alerta de "opcionais" entra entre o título e as ações, e o badge da
                    observação encolhe primeiro; a posição dele pela largura está no CSS
                    (.fin-pp__trecho-head--aberto). Fechado, sem o alerta, o cabeçalho é o de sempre. A
                    barra inteira abre e fecha, alerta incluído; pelo teclado, o botão do título. */}
                <div className={'fin-pp__trecho-head' + (t.expanded ? ' fin-pp__trecho-head--aberto' : '')}
                    style={{ cursor: 'pointer' }} onClick={() => alternarTrecho(t._key)}>
                    <button type="button" className="fin-pp__trecho-toggle"
                        onClick={e => { e.stopPropagation(); alternarTrecho(t._key); }}>
                        <span className="fin-pp__cphead-ic"><FaPlane size={14} /></span>
                        <span className="fin-pp__trecho-txt">
                            <span className="fin-pp__cphead-title">{tituloTrecho(t, i)}</span>
                            <span className="fin-pp__cphead-sub">{resumoTrecho(t)}</span>
                        </span>
                        <PillObservacao texto={t.observacao} />
                    </button>
                    {t.expanded && (
                        <div className="fin-pp__trecho-opcionais">
                            {alertaOpcionais(<>Todos os <span style={SUBLINHADO}>campos</span> abaixo são <strong >opcionais</strong></>)}
                        </div>
                    )}
                    <div className="fin-pp__trecho-acoes">
                        {acoesTrecho(t)}
                    </div>
                </div>
                {t.expanded && (
                    // A moldura do cartão (fundo, padding, borda) é daqui; o formulário só traz o
                    // conteúdo. No modal da malha ele entra sem isto.
                    <div className="fin-pp__trecho-body">
                    <FormTrecho trecho={t} editavel={editavel}
                        referencias={{ origemPai: origemPaiDe(t), destino: pedido.destino }}
                        heranca={{ dataIda: pedido.dataIda, dataRetorno: pedido.dataRetorno }}
                        origemSolicitacao={{
                            opcoes: origens.map(o => ({ chave: o._key, nome: o.origem })),
                            // Só o efeito da TELA: trocar a origem leva o card para outro grupo.
                            aoTrocar: (valor) => { abrirGrupos([valor]); setSeguirTrecho(t._key); },
                        }}
                        tetoPassageiros={tetoDePassageiros(t)}
                        observacao={{
                            id: 'observacao-trecho-' + t._key,
                            placeholder: 'Ex.: restrição de algum passageiro',
                        }}
                        onTrecho={(novo) => setTrechos(prev => prev.map(x => (x._key === t._key ? novo : x)))} />
                    </div>
                )}
            </div>
        );
    };

    const contagem = [
        `${origens.length} ${origens.length !== 1 ? 'origens' : 'origem'}`,
        trechos.length > 0 && `${trechos.length} ${trechos.length !== 1 ? 'trechos' : 'trecho'}`,
    ].filter(Boolean).join(' · ');

    // Fechado, o cabeçalho de "Destino e datas" mostra o preenchido no lugar do título; sem nada
    // preenchido, fica o título.
    const resumoPedido = [
        pedido.destino.trim() && `Destino: ${pedido.destino.trim()}`,
        dataLimpa(pedido.dataIda) && `Ida: ${dataLimpa(pedido.dataIda)}`,
        dataLimpa(pedido.dataRetorno) && `Volta: ${dataLimpa(pedido.dataRetorno)}`,
    ].filter(Boolean).join(' · ');
    const alternarPedido = () => setPedidoAberto(a => !a);

    // "Origens" e "Trechos" nas frases são atalho para a aba. Dentro do botão que abre "Destino e
    // datas" não pode ser outro botão: vira span, e o clique não chega ao acordeão.
    const linkAba = (chave, rotulo, dentroDeBotao = false) => (dentroDeBotao
        ? <span className="fin-pp__linkbtn fin-pp__linkbtn--texto" role="link"
            onClick={e => { e.stopPropagation(); setAba(chave); }}>{rotulo}</span>
        : <button type="button" className="fin-pp__linkbtn fin-pp__linkbtn--texto"
            onClick={() => setAba(chave)}>{rotulo}</button>);
    const totalPassageiros = origens.reduce((soma, o) => soma + (Number(o.quantidadePassageiros) || 0), 0);
    // A quantidade é opcional: o total soma as origens que a têm, e diz quantas estão sem.
    const origensSemQuantidade = origens.filter(o => o.quantidadePassageiros == null).length;

    return (
        <ErpTemplate processando={carregando}>
            <div className="main-content container-fluid fin-pp">

                {/* Cabeçalho enxuto do evento */}
                <CabecalhoEventoAereo
                    titulo="Solicitação de Malha Aérea"
                    nomeEvento={headerEvento.nomeEvento}
                    cliente={headerEvento.cliente}
                    solicitante={headerEvento.solicitante || (!PERFIS_AEREO.includes(perfil) ? nomeUsuario : '')}
                    dataEvento={headerEvento.dataEvento}
                    localEvento={headerEvento.localEvento}
                    tktMedio={headerEvento.tktMedio ?? null}
                    voosSolicitados={contagem}
                    atalhos={[
                        atendimento.atalho,
                        headerEvento.evento && {
                            rotulo: 'Solicitações',
                            titulo: 'Painel de Solicitações deste evento.',
                            to: '/cadastroEventoSolicitacoesAereo/' + headerEvento.evento,
                        },
                        {
                            rotulo: 'Malhas',
                            // Rascunho não tem malha: ela começa depois do envio ao Aéreo.
                            desabilitado: situacaoSolicitacao === 'pendente',
                            titulo: situacaoSolicitacao === 'pendente'
                                ? 'Rascunho ainda não tem malha — ela começa depois do envio ao Aéreo.'
                                : 'Abre o painel de Malhas desta solicitação.',
                            to: '/cadastroEventoMalhasAereo/' + idSolicitacao,
                        },
                        // A Importação das Opções é do RSVP.
                        cuidaDoPedido(perfil) && headerEvento.evento && {
                            rotulo: 'Importação das Opções',
                            titulo: 'Importa as opções das malhas aprovadas para o participante escolher.',
                            to: '/cadastroEventoPortacaoAereo/' + headerEvento.evento,
                        },
                    ]}
                />

                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Solicitação</h1>
                        <p className="fin-pp__subtitle">
                            Origens, Destino e Datas. Para detalhamento usar o campo de Observação ou a aba de Trechos.
                        </p>
                    </div>
                </div>

                <div className="fin-pp__tabs mb-3" role="tablist">
                    <button type="button" role="tab" aria-selected={aba === 'origens'}
                        className={'fin-pp__tab' + (aba === 'origens' ? ' is-active' : '')}
                        onClick={() => setAba('origens')}>
                        Origens ({origens.length})
                    </button>
                    <button type="button" role="tab" aria-selected={aba === 'trechos'}
                        className={'fin-pp__tab' + (aba === 'trechos' ? ' is-active' : '')}
                        onClick={() => setAba('trechos')}>
                        Trechos ({trechos.length})
                    </button>
                </div>

                <form onSubmit={e => { e.preventDefault(); salvar(); }}>
                    {aba === 'origens' && (
                        <>
                            <div className="fin-pp__card mb-3" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__trecho-head">
                                    <button type="button" className="fin-pp__trecho-toggle" aria-expanded={pedidoAberto}
                                        onClick={alternarPedido}>
                                        <span className="fin-pp__cphead-ic"><FaCalendarAlt size={14} /></span>
                                        <span className="fin-pp__trecho-txt">
                                            <span className="fin-pp__cphead-title">
                                                {!pedidoAberto && resumoPedido ? resumoPedido : 'Destino e datas'}
                                            </span>
                                            <span className="fin-pp__cphead-sub">
                                                {!pedidoAberto && pedido.observacao.trim()
                                                    ? pedido.observacao.trim()
                                                    : <>Iguais para todas as origens. Para mais detalhes ou exceções use a aba {linkAba('trechos', 'Trechos', true)}.</>}
                                            </span>
                                        </span>
                                    </button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm flex-shrink-0"
                                        aria-label={pedidoAberto ? 'Recolher destino e datas' : 'Abrir destino e datas'}
                                        onClick={alternarPedido}>
                                        {pedidoAberto ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                    </button>
                                </div>
                                {pedidoAberto && (
                                <div className="fin-pp__trecho-body">
                                {/* Colar as origens também aqui, para começar: destino, datas e
                                    origens se preenchem juntos. O card de Origens tem o dele,
                                    sempre visível. Escondida, a caixa continua montada: a janela
                                    dos separadores e o texto digitado não se perdem. */}
                                {editavel && (
                                    <div style={!perguntandoSeparador && (!origens.length || colarFocado || colarComTexto) ? { marginBottom: 16 } : { display: 'none' }}>
                                        <ColarOrigens existentes={origens} onAdicionar={adicionarOrigens} onAtualizarQuantidades={atualizarQuantidades}
                                            obrigatorio={!origens.length}
                                            onFocus={() => setColarFocado(true)}
                                            onBlur={() => setColarFocado(false)}
                                            onTexto={setColarComTexto}
                                            onPerguntando={aberta => {
                                                setPerguntandoSeparador(aberta);
                                                // Escondido, o input perde o foco sem garantia de blur.
                                                if (aberta) setColarFocado(false);
                                            }} />
                                    </div>
                                )}
                                <div className="fin-pp__filters">
                                    {/* Obrigatórios: destino, as duas datas e ao menos uma origem.
                                        As datas usam o rótulo do FinField, que leva o asterisco. */}
                                    <FinField label="Destino" grow required>
                                        <FinInput type="text" disabled={!editavel} minLength={2}
                                            placeholder={EXEMPLO_LOCAL}
                                            value={pedido.destino}
                                            onChange={e => setPedido(p => ({ ...p, destino: e.target.value }))} />
                                    </FinField>
                                    {/* As duas datas são um item só da linha: quebram juntas. A base
                                        é a largura das duas lado a lado, para a linha saber se cabem. */}
                                    <div className="d-flex flex-wrap align-items-end"
                                        style={{ gap: '14px 16px', flex: '1 1 416px', minWidth: 0 }}>
                                        <FinField label="Data de ida" required>
                                            <Data labelExterno label="" value={pedido.dataIda} disabled={!editavel}
                                                onChange={val => setPedido(p => ({ ...p, dataIda: val }))} />
                                        </FinField>
                                        <FinField label="Data de volta" required>
                                            <Data labelExterno label="" value={pedido.dataRetorno} disabled={!editavel}
                                                onChange={val => setPedido(p => ({ ...p, dataRetorno: val }))} />
                                        </FinField>
                                    </div>
                                </div>
                                <div className="fin-pp__filters" style={{ marginTop: 12 }}>
                                    {/* O campo é `observacao` no banco; na tela ele é "Observações e Premissas
                                        de Aéreo", o nome que a operação usa. */}
                                    <FinField label="Observações e Premissas de Aéreo" full>
                                        {/* O exemplo é para quem escreve (RSVP); na leitura do Aéreo, vazio é vazio. */}
                                        <FinTextarea rows={3} maxLength={2000} disabled={!editavel}
                                            placeholder={cuidaDaSolicitacao ? 'Ex.: chegar até as 10h do dia 12; voltar a partir das 17h do dia 14' : undefined}
                                            value={pedido.observacao}
                                            onChange={e => setPedido(p => ({ ...p, observacao: e.target.value }))} />
                                    </FinField>
                                </div>
                                </div>
                                )}
                            </div>

                            <div className="fin-pp__card mb-3" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__trecho-head">
                                    <span className="fin-pp__cphead-ic"><FaMapMarkerAlt size={14} /></span>
                                    <div className="fin-pp__trecho-txt" style={{ flex: 1 }}>
                                        <span className="fin-pp__cphead-title">Origens</span>
                                        <span className="fin-pp__cphead-sub">
                                            Cidades ou aeroportos de onde os participantes saem. Para detalhes ou agrupamento use a aba {linkAba('trechos', 'Trechos')}.
                                        </span>
                                    </div>
                                    <span className="fin-pp__grp-badge flex-shrink-0">
                                        {origens.length} {origens.length !== 1 ? 'origens' : 'origem'}
                                    </span>
                                    {/* O total da solicitação é a soma das origens com quantidade; a origem sem ela
                                        entra na conta de fora, para o número não parecer o total do evento. */}
                                    <span className="fin-pp__grp-badge flex-shrink-0">
                                        {origens.length > 0 && origensSemQuantidade === origens.length
                                            ? 'sem quantidade de passageiros'
                                            : `${totalPassageiros} ${totalPassageiros !== 1 ? 'passageiros' : 'passageiro'}${origensSemQuantidade
                                                ? ` (${origensSemQuantidade} ${origensSemQuantidade !== 1 ? 'origens' : 'origem'} sem quantidade)` : ''}`}
                                    </span>
                                </div>
                                {editavel && (
                                    <div style={{ padding: '0 20px 8px' }}>
                                        <ColarOrigens existentes={origens} onAdicionar={adicionarOrigens} onAtualizarQuantidades={atualizarQuantidades} obrigatorio={!origens.length} />
                                    </div>
                                )}
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ minWidth: 560, tableLayout: 'auto' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: 1 }}>#</th>
                                                <th>Origem</th>
                                                {/* Coluna do botão Observação, sem texto no cabeçalho. */}
                                                {editavel && <th style={{ width: 1 }} aria-label="Observação"></th>}
                                                <th style={{ width: 170 }}>
                                                    Qtd. passageiros
                                                </th>
                                                <th style={{ width: 1 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {origens.length === 0 && (
                                                <tr><td colSpan={editavel ? 5 : 4} className="fin-pp__empty">
                                                    {editavel ? 'Nenhuma origem. Cole a lista na caixa acima.' : 'Nenhuma origem.'}
                                                </td></tr>
                                            )}
                                            {origens.map((o, i) => {
                                                const presos = trechosPorOrigem.get(o._key) ?? 0;
                                                return (
                                                    <tr key={o._key}>
                                                        <td className="fin-pp__cell-muted" style={CELULA_NO_MEIO}>{i + 1}</td>
                                                        <td style={CELULA_NO_MEIO}>
                                                            {editavel ? (
                                                                <FinInput type="text" minLength={2} value={o.origem}
                                                                    onChange={e => atualizarOrigem(o._key, 'origem', e.target.value)} />
                                                            ) : <span className="fin-pp__cell-strong">{o.origem}</span>}
                                                        </td>
                                                        {/* A observação da origem mora no trecho dela: o botão leva à aba
                                                            Trechos, cria o trecho se a origem não tem nenhum e põe o foco na
                                                            observação. Na altura do input. */}
                                                        {editavel && (
                                                            <td style={CELULA_NO_MEIO}>
                                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                                    style={{ height: 44, whiteSpace: 'nowrap' }}
                                                                    title={presos
                                                                        ? 'Abre a observação do trecho desta origem, na aba Trechos'
                                                                        : 'Cria um trecho para esta origem, na aba Trechos, e abre a observação dele'}
                                                                    onClick={() => observarOrigem(o._key)}>
                                                                    <FaStickyNote size={11} />
                                                                    Observação
                                                                </button>
                                                            </td>
                                                        )}
                                                        <td style={CELULA_NO_MEIO}>
                                                            {editavel ? (
                                                                <FinNumber value={o.quantidadePassageiros ?? ''} decimalScale={0}
                                                                    thousandSeparator={false} allowNegative={false}
                                                                    onValueChange={v => atualizarOrigem(o._key, 'quantidadePassageiros', v.floatValue ?? null)} />
                                                            ) : <span className="fin-pp__cell-muted">{o.quantidadePassageiros ?? '—'}</span>}
                                                        </td>
                                                        <td style={CELULA_NO_MEIO}>
                                                            {editavel && (
                                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                                    title={presos ? 'Remove a origem; os trechos presos a ela ficam sem origem (vale ao salvar)' : 'Remove a origem (vale ao salvar)'}
                                                                    aria-label="Remover origem"
                                                                    onClick={() => removerOrigem(o._key)}>
                                                                    <FaTrash size={11} />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {aba === 'trechos' && (
                        <>
                            {/* Subtítulo, alerta e botão. A posição do alerta pela largura está no CSS
                                (.fin-pp__trechos-topo), no mesmo padrão do cabeçalho do trecho aberto. */}
                            <div className="fin-pp__trechos-topo mb-3">
                                <div className="fin-pp__trechos-topo-linha">
                                    <span className="fin-pp__cphead-sub fin-pp__trechos-topo-sub">
                                        Detalhamento por origem, se precisar: definir volta, destino, datas, horários e passageiros.
                                    </span>
                                    <div className="fin-pp__trechos-topo-alerta">
                                        {alertaOpcionais(<>Todos os <span style={SUBLINHADO}>campos</span> ou uso de <span style={SUBLINHADO}>trechos</span> são <strong >opcionais</strong></>)}
                                    </div>
                                    <div className="fin-pp__trechos-topo-acao">
                                        {/* O trecho de uma origem nasce pelo botão do acordeão dela; este
                                            cria o trecho solto. */}
                                        {editavel && (
                                            <button type="button" className="fin-pp__btn fin-pp__btn--pill"
                                                onClick={() => adicionarTrecho()}>
                                                <FaPlus size={12} />
                                                Trecho sem origem
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {gruposTrechos.length === 0 && (
                                <div className="fin-pp__card fin-pp__empty mb-3">Nenhuma origem. Cadastre alguma origem na aba {linkAba('origens', 'Origens')}.</div>
                            )}
                            {gruposTrechos.length > 0 && (
                                <div className="fin-pp__groups fin-pp__trecho-grupos mb-3" style={{ marginTop: 0 }}>
                                    {gruposTrechos.map(g => {
                                        const aberto = grupoEstaAberto(g);
                                        const n = g.trechos.length;
                                        return (
                                            <div key={chaveGrupo(g.chave)} className="fin-pp__grp">
                                                {/* A barra inteira abre e fecha; pelo teclado, o botão do nome.
                                                    Adicionar fica fora dele e não mexe no acordeão. Fechado, sem a
                                                    borda de baixo, que dobraria com a do grupo. */}
                                                <div className="fin-pp__grp-head" onClick={() => alternarGrupo(g)}
                                                    style={aberto ? undefined : { borderBottom: 0 }}>
                                                    <button type="button" className="fin-pp__trecho-toggle" aria-expanded={aberto}
                                                        onClick={e => { e.stopPropagation(); alternarGrupo(g); }}>
                                                        <span className={'fin-pp__grp-caret' + (aberto ? ' is-open' : '')}><FaChevronRight size={11} /></span>
                                                        <span className="fin-pp__grp-ic"><FaMapMarkerAlt size={13} /></span>
                                                        {/* Nome e passageiros são duas peças da mesma linha, não um texto só: assim o
                                                            "· N passageiros" nunca parte no meio — ou cabe ao lado do nome, ou desce
                                                            inteiro. Faltando espaço, quem cede é ELE, com reticências: o nome da
                                                            origem é o que identifica o grupo, e cortá-lo para caber a contagem
                                                            troca o essencial pelo acessório. */}
                                                        <span className="fin-pp__grp-name"
                                                            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 6, minWidth: 0 }}>
                                                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}>{g.nome}</span>
                                                            {/* Passageiros da origem, mais leve que o nome. O grupo "Sem origem" não tem. */}
                                                            {g.passageiros != null && (
                                                                <span style={{ fontWeight: 400, color: 'var(--fin-mt)', whiteSpace: 'nowrap',
                                                                    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 100 }}>
                                                                    {'· '}{g.passageiros} {g.passageiros !== 1 ? 'passageiros' : 'passageiro'}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span className="fin-pp__grp-badge">{n} trecho{n !== 1 ? 's' : ''}</span>
                                                    </button>
                                                    {editavel && (
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm flex-shrink-0"
                                                            title={g.chave ? `Novo trecho preso a ${g.nome}` : 'Novo trecho sem origem'}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                adicionarTrecho(g.chave);
                                                            }}>
                                                            <FaPlus size={11} />
                                                            Adicionar trecho
                                                        </button>
                                                    )}
                                                </div>
                                                {aberto && n === 0 && (
                                                    <div className="fin-pp__empty" style={{ padding: '14px 16px' }}>
                                                        Sem detalhamento: a origem pede ida e volta com o destino e as datas da solicitação.
                                                    </div>
                                                )}
                                                {aberto && g.trechos.map(renderTrecho)}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {/* O rodapé aparece para todos: a situação e a marcação de alteração informam o
                        Aéreo e Eventos também. Os botões são de quem cuida do pedido. */}
                    {(situacaoSolicitacao || cuidaDaSolicitacao) && (
                        <div className="row fixoRodape">
                            <div className="col-12 d-flex align-items-center justify-content-end gap-3">
                                {/* Situação do pedido: é o que explica por que o Enviar some depois
                                    de enviado. A marcação de alteração é a da linha do painel de
                                    Solicitações, e some quando o RSVP notifica. */}
                                {situacaoSolicitacao && (
                                    <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                                        <span className={'fin-pp__pill ' + (COR_SITUACAO_SOLICITACAO[situacaoSolicitacao] ?? 'is-muted')}>
                                            {ROTULO_SITUACAO_SOLICITACAO[situacaoSolicitacao] ?? situacaoSolicitacao}
                                        </span>
                                        {alteradoPor && (
                                            <span className="fin-pp__pill is-warn" title="A solicitação ou o TKT médio mudou depois do envio ao Aéreo">
                                                Alterado pelo {alteradoPor}
                                            </span>
                                        )}
                                    </span>
                                )}
                                {cuidaDaSolicitacao && !cancelada && (
                                    <button type="submit" disabled={salvando || enviando || notificando}
                                        // Um primário por vez, e o Salvar só perde o lugar para o ENVIAR: é ele
                                        // que passa a vez ao Aéreo, e não dá para desfazer. Notificar convive —
                                        // é avulso, se repete quantas vezes precisar, e roubar o azul do Salvar
                                        // fazia o botão do dia a dia sumir ao lado dele.
                                        className={'fin-pp__btn fin-pp__btn--tall' + (podeEnviar ? '' : ' fin-pp__btn--primary')}>
                                        <FaSave size={14} />
                                        {salvando ? 'Salvando...' : 'Salvar'}
                                    </button>
                                )}
                                {/* Depois do envio, o RSVP avisa o Aéreo a qualquer momento, com ou sem
                                    mudança. Salva antes, se a tela mudou; em alteração, devolve a
                                    solicitação ao Aéreo. */}
                                {podeNotificar && (
                                    <button type="button" onClick={iniciarNotificacao}
                                        disabled={salvando || notificando}
                                        title="Salva a solicitação, se mudou, e notifica o Aéreo da alteração."
                                        className="fin-pp__btn fin-pp__btn--tall">
                                        {notificando
                                            ? <span className="spinner-border spinner-border-sm" role="status" />
                                            : <FaBell size={13} />}
                                        {notificando ? 'Notificando...' : 'Notificar alteração'}
                                    </button>
                                )}
                                {/* O mesmo Reabrir do painel de Solicitações: cancelada ou concluída volta
                                    em alteração, com a observação no e-mail ao Aéreo. */}
                                {podeReabrir && (
                                    <button type="button" onClick={() => { setObservacaoReabertura(''); setConfirmarReabertura(true); }}
                                        disabled={salvando || notificando || reabrindo}
                                        title="Reabre a solicitação: volta em alteração, e o Aéreo é avisado."
                                        className="fin-pp__btn fin-pp__btn--tall">
                                        {reabrindo
                                            ? <span className="spinner-border spinner-border-sm" role="status" />
                                            : <FaUndo size={13} />}
                                        {reabrindo ? 'Reabrindo...' : 'Reabrir'}
                                    </button>
                                )}
                                {/* Do Aéreo: o pedido precisa mudar, e volta para o RSVP ajustar. As malhas
                                    ficam como estão. */}
                                {podePedirAlteracao && (
                                    <button type="button" onClick={() => { setObservacaoAlteracao(''); setConfirmarAlteracao(true); }}
                                        disabled={pedindoAlteracao}
                                        title={situacaoSolicitacao === 'alteracao'
                                            ? 'Já está em alteração — manda outra observação'
                                            : 'O pedido precisa mudar — as malhas ficam como estão'}
                                        className="fin-pp__btn fin-pp__btn--tall">
                                        {pedindoAlteracao
                                            ? <span className="spinner-border spinner-border-sm" role="status" />
                                            : <FaExchangeAlt size={13} />}
                                        {pedindoAlteracao ? 'Pedindo...' : 'Pedir alteração'}
                                    </button>
                                )}
                                {/* type="button": o Enviar não pode disparar o submit do form, que já
                                    salva sozinho — salvaria duas vezes. */}
                                {podeEnviar && (
                                    <button type="button" onClick={() => setConfirmarEnvio(true)} disabled={salvando || enviando}
                                        title="Salva a solicitação e a envia ao Aéreo, que passa a poder importar a malha."
                                        className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--primary">
                                        {enviando
                                            ? <span className="spinner-border spinner-border-sm" role="status" />
                                            : <FaPaperPlane size={13} />}
                                        {enviando ? 'Enviando...' : 'Enviar ao Aéreo'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </form>

                <ConfirmarAcaoModal
                    aberto={confirmarEnvio}
                    titulo="Enviar ao Aéreo?"
                    subtitulo="A solicitação é salva e o Aéreo passa a poder importar a malha."
                    notifica="O Aéreo será notificado por e-mail."
                    rotulo="Enviar"
                    icone="enviar"
                    processando={enviando}
                    onConfirmar={enviar}
                    onFechar={() => setConfirmarEnvio(false)}
                />

                <ConfirmarAcaoModal
                    aberto={confirmarNotificacao}
                    titulo="Notificar a alteração ao Aéreo?"
                    subtitulo={situacaoSolicitacao === 'alteracao'
                        ? 'A solicitação sai de Alteração e volta para o Aéreo, mesmo sem mudança.'
                        : 'A solicitação está salva. O Aéreo recebe o aviso para conferir a solicitação.'}
                    notifica="O Aéreo será notificado por e-mail, com a observação."
                    rotulo="Notificar"
                    icone="enviar"
                    processando={notificando}
                    confirmarPreso={!observacaoNotificacao.trim()}
                    onConfirmar={notificarAlteracao}
                    onFechar={() => setConfirmarNotificacao(false)}>
                    <ObservacaoObrigatoria valor={observacaoNotificacao}
                        onChange={setObservacaoNotificacao} placeholder="O que mudou na solicitação" />
                </ConfirmarAcaoModal>

                <ConfirmarAcaoModal
                    aberto={confirmarReabertura}
                    titulo="Reabrir a solicitação?"
                    subtitulo="Ela volta em alteração: o RSVP ajusta e reenvia ao Aéreo."
                    notifica="O Aéreo será notificado por e-mail, com a observação."
                    rotulo="Reabrir"
                    icone="alteracao"
                    processando={reabrindo}
                    confirmarPreso={!observacaoReabertura.trim()}
                    onConfirmar={reabrir}
                    onFechar={() => setConfirmarReabertura(false)}>
                    <ObservacaoObrigatoria valor={observacaoReabertura} onChange={setObservacaoReabertura} />
                </ConfirmarAcaoModal>

                <ConfirmarAcaoModal
                    aberto={confirmarAlteracao}
                    titulo={situacaoSolicitacao === 'alteracao' ? 'Pedir alteração de novo?' : 'Pedir alteração da solicitação?'}
                    subtitulo={situacaoSolicitacao === 'alteracao'
                        ? 'Ela continua em alteração — só a observação nova é enviada.'
                        : 'Ela volta para o RSVP ajustar. As malhas ficam como estão, e seguem abertas ao Aéreo.'}
                    notifica="O RSVP recebe o e-mail com a observação, com a caixa do Aéreo em cópia."
                    rotulo="Pedir alteração"
                    icone="alteracao"
                    processando={pedindoAlteracao}
                    confirmarPreso={!observacaoAlteracao.trim()}
                    onConfirmar={pedirAlteracao}
                    onFechar={() => setConfirmarAlteracao(false)}>
                    <ObservacaoObrigatoria valor={observacaoAlteracao} onChange={setObservacaoAlteracao} />
                </ConfirmarAcaoModal>
            </div>
            {atendimento.modal}
        </ErpTemplate>
    );
}
